import { useMemo, useRef, useState, type KeyboardEvent } from "react";

import type { SearchEntry, SearchResult, SiteSearch } from "./search.types";
import { rankEntries } from "./search.utils";

// Max matches shown in the dropdown.
const MAX_RESULTS = 8;

type SiteSearchOptions<TEntry extends SearchEntry> = {
	// Already translated and already filtered by the app.
	pages: readonly TEntry[];
	// The app owns its router, so it passes the navigation in.
	navigate: (to: TEntry["to"]) => void;
};

// Drives an app-shell search bar: it holds the query, ranks the site pages, and
// navigates on select.
export function useSiteSearch<TEntry extends SearchEntry>({
	pages,
	navigate,
}: SiteSearchOptions<TEntry>): SiteSearch<TEntry> {
	const [query, setQuery] = useState("");
	const [open, setOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);

	const results = useMemo(() => rankEntries(pages, query).slice(0, MAX_RESULTS), [pages, query]);

	function reset() {
		setQuery("");
		setOpen(false);
		setActiveIndex(0);
	}

	function select(result: SearchResult<TEntry> | undefined) {
		if (!result) return;
		navigate(result.entry.to);
		reset();
		// Drop focus so the bar deselects after navigating to the page.
		inputRef.current?.blur();
	}

	function onQueryChange(value: string) {
		setQuery(value);
		setOpen(true);
		setActiveIndex(0);
	}

	function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
		if (!open || results.length === 0) return;
		switch (e.key) {
			case "ArrowDown":
				e.preventDefault();
				setActiveIndex((i) => (i + 1) % results.length);
				break;
			case "ArrowUp":
				e.preventDefault();
				setActiveIndex((i) => (i - 1 + results.length) % results.length);
				break;
			case "Enter":
				e.preventDefault();
				select(results[activeIndex]);
				break;
			case "Escape":
				e.preventDefault();
				reset();
				break;
		}
	}

	// The app binds its own hotkey to this.
	function focus() {
		setOpen(true);
		inputRef.current?.focus();
		inputRef.current?.select();
	}

	return {
		query,
		open,
		setOpen,
		results,
		activeIndex,
		setActiveIndex,
		onQueryChange,
		onKeyDown,
		select,
		inputRef,
		focus,
	};
}
